/**
 * AuthProvider — React context that exposes auth state to the component tree.
 *
 * This is a thin wrapper around `useAuthStore` that provides:
 *  - A React context for `useAuth()` consumers
 *  - Derived convenience flags (isAuthenticated, isLoading)
 *  - The `logout()` action wired to all necessary cleanup
 *
 * Replaces: the scattered reads of `useInitializationStatus` across the app.
 */

import { createContext, useCallback, useMemo, type ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useShallow } from "zustand/react/shallow"

import { useClientSessionSetting } from "@be-miniapp/shared/stores/client-session-setting"
import { useInitializationStatus } from "@be-miniapp/shared/stores/initialized"
import { useRideOptionFormState } from "@be-miniapp/shared/stores/ride-option-form-state"

import { PartnerInvoiceCleanup } from "@/hooks/use-partner-invoice-cleanup"
import { useClientInfo } from "@/store/client-info"
import { useUserStore } from "@/store/user-session"

import { useAuthStore } from "./auth-store"
import { AUTH_STATUS } from "./auth-types"
import type { AuthContextValue } from "./auth-types"
import { clearPersistedSession } from "./oauth-handler"

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null)
AuthContext.displayName = "AuthContext"

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode
}

function AuthProvider({ children }: AuthProviderProps) {
  const state = useAuthStore(
    useShallow((s) => ({
      status: s.status,
      session: s.session,
      error: s.error,
    }))
  ) as import("./auth-types").AuthState

  const resetAuthStore = useAuthStore((s) => s.reset)

  // External store actions needed for a full logout
  const queryClient = useQueryClient()
  const setAccessToken = useClientInfo((s) => s.setAccessToken)
  const logoutUserStore = useUserStore((s) => s.logout)
  const resetUserToken = useUserStore((s) => s.setToken)
  const resetClientSetting = useClientSessionSetting((s) => s.reset)
  const resetFormState = useRideOptionFormState((s) => s.reset)
  const setLegacyInitialized = useInitializationStatus(
    (s) => s.setIsInitialized
  )

  const logout = useCallback(() => {
    // 1. Clear persisted token
    clearPersistedSession()

    // 2. Reset all external stores (mirrors old useLogout)
    setAccessToken(undefined)
    resetUserToken(null)
    logoutUserStore()
    resetClientSetting()
    resetFormState()

    // 3. Reset auth store → UNINITIALIZED
    resetAuthStore()

    // 4. Sync legacy initialization store
    setLegacyInitialized(false, false)

    // 5. Invalidate all cached queries
    setTimeout(() => {
      queryClient.invalidateQueries()
    }, 1000)
  }, [
    resetAuthStore,
    setAccessToken,
    resetUserToken,
    logoutUserStore,
    resetClientSetting,
    resetFormState,
    setLegacyInitialized,
    queryClient,
  ])

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      isAuthenticated: state.status === AUTH_STATUS.AUTHENTICATED,
      isLoading:
        state.status === AUTH_STATUS.RESTORING_SESSION ||
        state.status === AUTH_STATUS.AUTHENTICATING,
      session: state.session,
      logout,
    }),
    [state, logout]
  )

  return (
    <AuthContext.Provider value={value}>
      <PartnerInvoiceCleanup />
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
